import posthog from '@leyan/lytics';
import { courierClient, ImResponse, resolve } from '.';
import { stickers } from 'views/Conversation/Chat/components/MessageEditor/Toolbar/KwaiStickerPicker';

export type TransFrom =
  | 'chat'
  | 'messageEditor'
  | 'side'
  | 'drawer'
  | 'translateTool'
  | 'gpt4TranslateTool'
  | 'feedback'
  | 'phrase'
  | 'review'
  | 'regions'
  | 'singleClass'
  | 'todoBundle'
  | 'beisiTag'
  | 'videoText'
  | 'supplementKnowledge'
  | 'qaPairBuyerQuestion'
  | 'mercadoQA'
  | 'csatData';

const getTargetLangParam = (target: General.Language) => (/^en-\w+$/.test(target) ? 'en' : target);

const getTranslateReplaceContent = (text: string) => {
  let index = 0;

  const replaces: string[] = [];

  const content = text.replace(/(\[.*?\])/g, (match) => {
    if (stickers.includes(match)) {
      replaces.push(match);
      return ` @x${index++} `;
    }
    return match;
  });

  return {
    content,
    replaces,
  };
};
const getTranslatedReplaceResult = (result: string, replaces: string[]) => {
  return replaces.reduce((res, replace, index) => {
    return res.replace(`@x${index}`, replace);
  }, result);
};

export function overseaTranslate(
  text: string,
  target: General.Language,
  from: TransFrom,
  region?: General.UpperCaseRegion,
  platform?: General.PlatformUpperCaseTunnel,
  signal?: AbortSignal,
): Promise<string>;
export function overseaTranslate(
  texts: string[],
  target: General.Language,
  from: TransFrom,
  region?: General.UpperCaseRegion,
  platform?: General.PlatformUpperCaseTunnel,
  signal?: AbortSignal,
): Promise<string[]>;

export function overseaTranslate(
  text: string | string[],
  target: General.Language,
  from: TransFrom,
  region?: General.UpperCaseRegion,
  platform?: General.PlatformUpperCaseTunnel,
  signal?: AbortSignal,
) {
  const texts = Array.isArray(text) ? text : [text];
  const translateReplaceContents = texts.map(getTranslateReplaceContent);
  const sourceText = translateReplaceContents.map((_) => _.content);

  const body = {
    sourceText,
    source: '',
    target: getTargetLangParam(target),
    region,
    platform,
  };

  return resolve(
    courierClient.post<ImResponse<{ translatedList: string[] }>>(`/translate?from=${from}`, body, {
      signal,
    }),
  ).then(({ translatedList }) => {
    const translatedReplaceResults = translatedList.map((result, index) =>
      getTranslatedReplaceResult(result, translateReplaceContents[index].replaces),
    );

    if (Array.isArray(text)) {
      return translatedReplaceResults;
    }

    return translatedReplaceResults[0];
  });
}

export type TranslateMode = 'CHAT_GPT' | 'GOOGLE' | 'DEEPSEEK' | 'CHATPLUSAI';

export interface TranslateOptions {
  isUseRedisCache?: boolean;
  from?: TransFrom;
  platform?: General.PlatformUpperCaseTunnel;
  region?: General.UpperCaseRegion;
}

export function modeTranslate(
  text: string,
  target: General.Language,
  mode: TranslateMode,
  options?: TranslateOptions,
): Promise<string>;
export function modeTranslate(
  texts: string[],
  target: General.Language,
  mode: TranslateMode,
  options?: TranslateOptions,
): Promise<string[]>;

export function modeTranslate(
  texts: string | string[],
  target: General.Language,
  mode: TranslateMode,
  options: TranslateOptions = {},
) {
  const { isUseRedisCache = true, from = 'chat', platform, region } = options;

  const translateTexts = Array.isArray(texts) ? texts : [texts];
  const translateReplaceContents = translateTexts.map(getTranslateReplaceContent);

  const body = {
    originals: translateReplaceContents
      .map((_) => _.content)
      .map((text) => {
        return {
          original: text,
          targetLanguage: getTargetLangParam(target),
        };
      }),
    mode,
    isUseRedisCache,
    from,
    platform,
    region,
  };

  interface Response {
    translatedText: {
      translatedText: string;
    }[];
  }

  return resolve(courierClient.post<ImResponse<Response>>('/translate/force_mode/v2', body))
    .then((res) => {
      const translatedReplaceResults = res.translatedText.map(({ translatedText }, index) =>
        getTranslatedReplaceResult(translatedText, translateReplaceContents[index].replaces),
      );

      if (Array.isArray(texts)) {
        return translatedReplaceResults;
      }

      return translatedReplaceResults[0];
    })
    .catch((e) => {
      posthog.capture('$transerror', {
        error: JSON.stringify({
          to: target,
          from,
          texts,
        }),
      });

      return Promise.reject(e);
    });
}

export interface ReportErrorTranslateBody {
  original: string;
  targetLanguage: General.Language;
  translatedText: string;
  translateType?: TranslateMode;
  region: General.UpperCaseRegion;
  from: TransFrom;
}

export function reportErrorTranslate(body: ReportErrorTranslateBody) {
  return courierClient.post('/translate/report_error_translate', {
    ...body,
    targetLanguage: getTargetLangParam(body.targetLanguage),
  });
}
